import { CurrencyPipe } from '@angular/common';
import { Component, inject, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { FormComponent, FormHandlingComponent, PageComponent } from '@tytapp/common';
import { LocalStorageService } from 'angular-2-local-storage';
import { ApiGiftItem, ApiGiftReceipt, ApiPostGifts, ApiUser, GiftsApi } from 'api';
import { environment } from 'environments/environment';
import { UserService } from 'user';
import { CartItem, CartService } from '../cart.service';
import { CreditCardSelectorComponent } from '../credit-card-selector/credit-card-selector.component';

const FIXED_PERIOD_FORMAT = '{period} of {product} for {price}';
const RECURRING_PERIOD_FORMAT = '{product} {period} for {price}';

interface GiftTheme {
    id: string;
    name: string;
    image: string;
    accessibleName: string;
}

interface GiftablePeriod {
    id: string;
    name: string;
    type: 'fixed' | 'recurring';

    /**
     * Specify how to combine the product name with this period. Use {product} for
     * the product name and {period} for the period name, and {price} for the price.
     */
    productOptionLabelFormat: string;
    requiresAccount?: boolean;
    priceSuffix?: string;
    skuOverride?: string;
}

interface GiftableProduct {
    /**
     * ID of this product. This is the base product SKU (insider, activist, etc)
     */
        id: string;

    /**
     * Set this to override the base product SKU used for fixed term gifts
     */
    fixedSku?: string;

    /**
     * Set this to override the base product SKU used for recurring gifts
     */
    recurringSku?: string;
    name: string;
    benefits: string[];
}

export interface GiftItemData extends ApiGiftItem {
    price: number;
    charge_type: string;
}

export interface GiftItem extends CartItem {
    themeName: string;
    recipientEmail: string;
    recipientName: string;
    productSKU: string;
    purchaserName: string;
    months?: number;
    deliverImmediately: boolean;
    deliverAt?: string;
    message: string;
}

@Component({
    selector: 'app-gifts',
    templateUrl: './gifts.component.html',
    styleUrls: ['./gifts.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class GiftsComponent extends PageComponent implements FormHandlingComponent {
    private giftsApi = inject(GiftsApi);
    private localStorage = inject(LocalStorageService);
    private userService = inject(UserService);
    private cartService = inject(CartService);
    private route = inject(ActivatedRoute);

    cart = this.cartService.getCart<GiftItem>('gifts');

    giftItemUuid = '';

    user: ApiUser;

    submitting = false;
    editCart = false;
    step = 'one';

    validityError: boolean = false;

    @ViewChild('ccElement')
    ccElement: CreditCardSelectorComponent;

    emailTheme = null;

    get signedIn() {
        return !!this.user;
    }

    durations = [3, 6, 12];
    products: GiftableProduct[] = [
        {
            id: 'insider',
            name: 'Insider',
            benefits: [
                'Access to TYT member shows on our app and TYT.com',
                'Exclusive Post-Game extras',
                'Special behind-the-scenes content',
                'Opportunity to interact with hosts and celebrity guests during our live TYTownhalls',
                'Members-only merchandise and discounts at ShopTYT.com',
            ]
        },
        {
            id: 'activist',
            name: 'Activist',
            benefits: [
                'Everything in the TYT Insider package, PLUS:',
                'First invites and select Activists-only TYTownhalls',
                'ShopTYT merchandise code ($25 value)*',
                'First access to virtual and physical TYT event tickets',
                '* Monthly Activist members will be eligible for the credit after 12 consecutive monthly payments as a paid member at the Activist level.'
            ]
        }
    ];

    periods: GiftablePeriod[] = [
        {
            id: 'fixed-3',
            type: 'fixed',
            name: '3 months',
            productOptionLabelFormat: FIXED_PERIOD_FORMAT
        },
        {
            id: 'fixed-6',
            type: 'fixed',
            name: '6 months',
            productOptionLabelFormat: FIXED_PERIOD_FORMAT
        },
        {
            id: 'fixed-12',
            type: 'fixed',
            name: '12 months',
            productOptionLabelFormat: FIXED_PERIOD_FORMAT
        },
        {
            id: 'monthly',
            type: 'recurring',
            name: 'Monthly',
            priceSuffix: '/mo',
            requiresAccount: true,
            productOptionLabelFormat: RECURRING_PERIOD_FORMAT
        },
        {
            id: 'annually',
            type: 'recurring',
            name: 'Annually',
            priceSuffix: '/yr',
            requiresAccount: true,
            productOptionLabelFormat: RECURRING_PERIOD_FORMAT
        },
    ]

    prices = {
        'insider-fixed-3': 1497,
        'insider-fixed-6': 2994,
        'insider-fixed-12': 5988,
        'insider-monthly': 499,
        'insider-annually': 4999,

        'activist-fixed-3': 3000,
        'activist-fixed-6': 6000,
        'activist-fixed-12': 12000,
        'activist-monthly': 1000,
        'activist-annually': 10000,
    };

    // When the prices here differ from the prices above, a crossed out price (from this table) is shown.
    previousPrices = {
    };

    priceFor(product: GiftableProduct, period: GiftablePeriod) {
        return this.prices[this.skuFor(product, period)] / 100.0;
    }

    previousPriceFor(product: GiftableProduct, period: GiftablePeriod) {
        const key = this.skuFor(product, period);
        return this.previousPrices[key] ? this.previousPrices[key] / 100.0 : null;
    }

    skuFor(product: GiftableProduct, period: GiftablePeriod) {
        if (period.type === 'fixed')
        return `${product.fixedSku || product.id}-${period.id}`;
        else if (period.type === 'recurring')
        return `${product.recurringSku || product.id}-${period.id}`;

        return `${product.id}-${period.id}`;
    }

    isSkuSelected(product: GiftableProduct, period: GiftablePeriod) {
        if (!this.itemUnderEdit)
            return false;

        return this.itemUnderEdit.productSKU === this.skuFor(product, period);
    }

    themes: GiftTheme[] = [
        {
            id: 'cenk',
            name: 'Cenk',
            accessibleName: 'Cenk Uygur',
            image: '/assets/gift/themes-2023/cenk-uygur.small.jpg'
        },
        {
            id: 'ana',
            name: 'Ana',
            accessibleName: 'Ana Kasparian',
            image: '/assets/gift/themes-2023/ana-kasparian.small.jpg'
        },
        {
            id: 'john',
            name: 'John',
            accessibleName: 'John Iadarola',
            image: '/assets/gift/themes-2023/john-iadarola.small.jpg'
        },
        {
            id: 'jayar',
            name: 'Jayar',
            accessibleName: 'Jayar Jackson',
            image: '/assets/gift/themes-2023/jayar-jackson.small.jpg'
        },
        {
            id: 'richey',
            name: 'Dr. Richey',
            accessibleName: 'Dr. Rashad Richey',
            image: '/assets/gift/themes-2023/rashad-richey.small.jpg'
        },
        // {
        //   id: 'mariguana',
        //   name: 'Mariguana',
        //   image: 'https://images.ctfassets.net/uoaa2cfwzbej/14LfwU66raSCWEsCae8yG0/a18aecd5696f0472629b7356de2e4125/mariguana.png?w=300'
        // }
    ];

    async init() {
        this.pageTitle = 'Gifts';

        await this.cartService.ready;
        this.itemUnderEdit = this.newItem();

        let routeParams: ParamMap;

        let updateRoute = async () => {
            if (!routeParams)
                return;

            await this.cartService.ready;

            let step = routeParams.get('step');
            let item = routeParams.get('item');

            this.itemUnderEdit = this.newItem();
            if (step == 'cart') {
                this.step = 'two';
                if (this.cart.items.length === 0)
                    this.router.navigateByUrl('/gift/add', { replaceUrl: true });
            } else if (step == 'add') {
                this.addAnotherGift();
            } else if (step == 'edit') {
                this.editGiftItem(this.cart.items.find(x => x.uuid == item));
            } else if (step == 'complete') {
                this.step = 'three';
            } else {
                if (this.cart.items.length === 0)
                    this.router.navigateByUrl(`/gift/add`, { replaceUrl: true });
                else
                    this.router.navigateByUrl(`/gift/cart`, { replaceUrl: true });
            }

        }

        this.route.paramMap.subscribe(async params => {
            routeParams = params;
            updateRoute();
        });

        this.subscribe(this.userService.userChanged, async user => {
            this.user = user;

            await this.cartService.ready;

            updateRoute();
        });

        if (this.cart.items.length > 0) {
            this.step = 'two';
        }

    }

    newItem() {
        return this.itemUnderEdit = this.cartService.newItem('gift', {
            purchaserName: this.user?.profile?.first_name ?? this.user?.profile?.display_name ?? '',
            deliverImmediately: true,
            price: 0,
            themeName: 'cenk',
            recipientEmail: '',
            recipientName: '',
            productSKU: '',
            message: ''
        });
    }

    get appUrl() {
        return environment.urls.app;
    }

    resetFields() {
        this.itemUnderEdit = this.newItem();
        this.form?.clearErrors();
        this.hideError();
        this.selectProduct = false;
        this.selectTheme = false;
        this.emailTheme = null;

    }

    onCartClick() {
        this.router.navigate([`/gift/cart`]);
    }

    getThemeName(id: string) {
        let theme = this.themes.find(x => x.id == id);

        if (!theme)
            return '(Unknown)';

        return theme.name;
    }

    giftProductName(giftItem: GiftItem) {
        let duration = ''
        if (!giftItem.recurs) {
            duration = `${giftItem.months} Months`
        } else {
            duration = `Recurring ${giftItem.recurs.replace(/^./, x => x.toUpperCase())}`;
        }

        let productID = giftItem.productSKU.split('-')[0];
        let product = this.products.find(x => x.id === productID);

        return `${product?.name ?? productID} (${duration})`
    }

    selectMembershipOption(product: GiftableProduct, period: GiftablePeriod) {
        this.itemUnderEdit.productSKU = this.skuFor(product, period);
        this.itemUnderEdit.price = this.priceFor(product, period);
        this.selectProduct = false;
    }

    addAnotherGift() {
        this.step = 'one'
        this.resetFields();
    }

    removeGiftItem(item) {
        this.cartService.removeItem(this.cart, item);

        if (this.cart.items.length === 0) {
            this.step = 'one';
            this.router.navigate(['/gift/add']);
        }
    }

    itemUnderEdit: GiftItem;

    cloneGiftItem(item: GiftItem) {
        return JSON.parse(JSON.stringify(item));
    }

    editGiftItem(giftItem: GiftItem) {
        this.itemUnderEdit = this.cloneGiftItem(giftItem);

        if (!this.itemUnderEdit.recurs) {
            let type = this.itemUnderEdit.productSKU.replace('-monthly', '');
            this.itemUnderEdit.productSKU = `${type}-fixed-${this.itemUnderEdit.months}`;
        }

        this.selectTheme = false;
        this.selectProduct = false;
        this.itemUnderEdit.deliverImmediately ??= !giftItem.deliverAt;
        this.editCart = true;
        this.step = 'one'
    }

    selectTheme: boolean = false;
    selectProduct: boolean = false;

    setEmailTheme(themeName: string) {
        if (!this.itemUnderEdit)
            return;

        this.itemUnderEdit.themeName = themeName;
        this.selectTheme = false;
    }

    isValidEmail(email: string) {
        if (!email)
            return false;

        if (email.includes(' '))
            return false;

        if (email.endsWith('.'))
            return false;

        if (!email.includes('@'))
            return false;

        if (email.endsWith('@'))
            return false;

        if (!/^[^@]+@[^@]*\.[^@]*$/.test(email))
            return false;

        return true;
    }

    @ViewChild('form') form: FormComponent;

    async addToCart() {
        this.form?.clearErrors();
        this.hideError();

        let hasErrors = false;

        //if (!this.validate())
        //  return;

        if (!this.itemUnderEdit.deliverImmediately) {
            if (!this.itemUnderEdit.deliverAt) {
                this.form.addFieldError('deliver-at', 'Please provide the delivery date for your gift')
                hasErrors = true;
            }

            let date = new Date(this.itemUnderEdit.deliverAt);
            if (date.getTime() < new Date().getTime()) {
                this.form.addFieldError('deliver-at', 'Delivery date must be in the future')
                hasErrors = true;
            }
        }

        if (!this.itemUnderEdit.themeName) {
            this.itemUnderEdit.themeName = 'cenk';
        }

        if (!this.itemUnderEdit.recipientEmail || !this.isValidEmail(this.itemUnderEdit.recipientEmail)) {
            this.form.addFieldError('recipient-email', 'Please provide a valid recipient email');
            hasErrors = true;
        }

        if (!this.itemUnderEdit.productSKU) {
            this.selectProduct = true;
        }
        if (!this.itemUnderEdit.recipientName) {
            this.form.addFieldError('recipient-name', 'Who do you want to send this to?');
            hasErrors = true;
        }

        if (this.user) {
            this.cart.email = this.user.email;
        } else {
            if (!this.cart.email || !this.isValidEmail(this.cart.email)) {
                this.form.addFieldError('purchaser-email', 'Please provide a valid email address');
                hasErrors = true;
            }
        }

        if (!this.itemUnderEdit.purchaserName) {
            this.form.addFieldError('purchaser-name', 'Please enter your name as it should appear on your gift');
            hasErrors = true;
        }

        if (!this.itemUnderEdit.recipientName) {
            this.form.addFieldError('recipient-email', 'Please provide a valid recipient email');
            hasErrors = true;
        }

        if (!this.itemUnderEdit.recipientName) {
            this.form.addFieldError('recipient-email', 'Please provide a valid recipient email');
            hasErrors = true;
        }

        if (hasErrors)
            return;

        // check eligibility
        // response = await this.usersApi.giftEligibleByEmail(this.recipientEmail).toPromise();
        // if (!response.eligible) {
        //   this.showError("This email is not an eligible recipient");
        //   return;
        // }

        let item: GiftItem = this.cloneGiftItem(this.itemUnderEdit);
        let options = item.productSKU.split('-');

        if (options[1] === 'fixed') {
            // Fixed
            item.productSKU = `${options[0]}-monthly`;
            item.recurs = undefined;
            item.months = Number(options[2]);
        } else {
            // Renewing
            item.recurs = <'monthly' | 'annually'>options[1];
            item.months = 0;
        }

        item.deliverImmediately ??= !item.deliverAt;

        this.cartService.updateItem(this.cart, item);
        this.resetFields();

        this.step = 'two';
        this.form?.clearErrors();
        this.hideError();

        this.router.navigate(['/gift/cart']);
    }

    saveCart() {
        if (this.user)
            this.localStorage.set('purchasingUserID', this.user.uuid);
        else
            this.localStorage.remove('purchasingUserID');

        this.localStorage.remove('cart');
        this.localStorage.set('cart', this.cart);
    }

    get newCardMessage() {
        if (this.cart.items.some(x => x.recurs)) {
            return `Because you are purchasing a renewable gift, `
                + `this card will be used for renewing your own membership in `
                + `addition to being used to renew your gift memberships.`
                ;
        } else {
            return null;
        }
    }

    async proceedToPayment() {
        // CRITICAL SECTION: NO ASYNC
        if (this.submitting)
            return;
        this.submitting = true;
        // END CRITICAL SECTION: NO ASYNC

        if (!this.ccElement.isValid()) {
            this.submitting = false;
            return;
        }

        let cardSource = await this.ccElement.getSource();
        if (!cardSource) {
            this.submitting = false;
            return;
        }

        // MAKE PURCHASE WITH CURRENT STRIPE PAYMENT

        try {
            let purchaseResponse = await this.giftsApi.purchase({
                purchaser_email: this.cart.email,
                gateway_name: 'stripe',
                gateway_data: JSON.stringify({ token: cardSource }),
                gift_items: this.cart.items.map(cartItem => (<ApiGiftItem>{
                    product_sku: cartItem.productSKU.replace(/-/g, '_'),
                    recipient_name: cartItem.recipientName,
                    recipient_email: cartItem.recipientEmail,
                    purchaser_name: cartItem.purchaserName,
                    purchaser_email: this.user?.email,
                    gift_message: cartItem.message,
                    theme_name: cartItem.themeName,
                    recurring: cartItem.recurs ? true : false,
                    sponsorship: false,
                    purchased_quantity: cartItem.quantity || 1,
                    // Old versions of the frontend sent US date format. New versions use the <input type="date"> which sends 
                    // international date format, but the backend is not yet updated. Once Platform can accept either date format,
                    // this should be removed.
                    deliver_at: cartItem.deliverImmediately 
                        ? undefined 
                        : cartItem.deliverAt?.replace(/(\d\d\d\d)-(\d\d)-(\d\d)/, '$2/$3/$1'),
                    months: cartItem.months,
                }))
            }).toPromise();
            this.cartService.clear(this.cart);
            this.submitting = false;

            try {
                await this.userService.sendBeacons('Gift Order Purchased', () => {
                    this.userService.sendBeaconGA4('purchase', {
                        purchase_type: 'gift',
                        offer_presentation: 'page',
                        app: 'tyt_accounts',
                        path: 'gifts',
                        transaction_id: purchaseResponse.uuid,
                        currency: 'USD',
                        value: Number(purchaseResponse.total_amount) / 100,
                        items: purchaseResponse.gift_items.map(item => ({
                            item_id: `${item.product_sku}:${item.recurring ? 'recurring' : `fixed:${item.months}_month`}`,
                            item_name: `${item.recurring ? `Recurring Gift (${item.product_sku})` : `${item.months} Month Gift (${item.product_sku})`}`,
                            item_brand: 'TYT',
                            item_category: 'Membership',
                            item_category2: 'Gifts',
                            item_category3: item.recurring ? 'Recurring Gifts' : 'Fixed-Term Gifts',
                            price: Number(item.dollar_price),
                            quantity: item.purchased_quantity
                        }))
                    });

                    this.userService.sendBeaconFB('Purchase',
                        'gift_order_purchased',
                        {
                            purchase_type: 'membership_gift',
                            purchase_flow: 'accounts.tyt.com/gift',
                            content_type: 'product',
                            user_uuid: this.user ? this.user.uuid : 'none',
                            contents: this.cart.items.map(x => ({
                                id: `${x.productSKU}/${x.recurs ? 'monthly' : 'fixed'}/${x.recurs ? '0' : x.months}`,
                                quantity: 1
                            })),
                            value: this.cart.totals.total,
                            currency: 'USD'
                        }
                    )
                });
            } catch (e) {
                console.error(`Failed to send analytics beacons:`);
                console.error(e);
            }

            this.router.navigate(['/gift/complete']);
        } catch (e) {
            let response = e.json ? e.json() : e;

            console.error("Error occurred while performing gift purchase request")
            console.error(e);

            if (response.error === 'invalid') {
                this.showError(
                    "Could not place order, please check your credit card details and please note " 
                    + "that gifts cannot be sent to existing TYT members. If you continue to have problems, " 
                    + "please contact support@tytnetwork.com."
                );
                this.validityError = true;
            } else if (response.error === 'contact-support') {
                this.showError("An error occurred while processing this request. Please contact support at support@tytnetwork.com for further assistance.");
            } else if (response.error === 'failure') {
                this.showError("An error occurred while processing this request. Your card was not charged. Please try again later.");
            } else if (response.code === 'invalid-order') {
                this.showError("Uh oh, a problem occurred submitting your request. Please try again later or contact TYT Support (code: invalid_order).")
            } else {
                this.showError(response.message || response.error || "An error has occurred");
            }
            this.submitting = false;
        }
    }

    cancelAddToCart() {
        this.resetFields();

        if (this.cart.items.length > 0) {
            this.router.navigate(['/gift/cart']);
        } else {
            this.router.navigate(['/account']);
        }
    }

    submit() {

    }

    priceA11y(product: GiftableProduct, period: GiftablePeriod) {
        return `${period.productOptionLabelFormat
            .replace(/\{product\}/g, product.name)
            .replace(/\{period\}/g, period.name)
            .replace(/\{price\}/g, `${new CurrencyPipe('en-US').transform(this.priceFor(product, period), 'USD')}${period.priceSuffix || ''}`)
            }`;
    }

    benefitsA11y(product: GiftableProduct) {
        return `Benefits of ${product.name} membership: ${product.benefits.join(', ')}`;
    }

    themeButtonA11y(theme: GiftTheme) {
        let selected = this.itemUnderEdit.themeName === theme.id;

        return `${theme.accessibleName} theme, ${selected ? 'selected' : 'not selected'}`;
    }
}
